import { Activity, Radio, RotateCw, WifiOff } from 'lucide-react';
import { useEffect, useState } from 'react';

import type { ConnectionStatus } from '@/services/tradeXRadarSignalRService';
import { formatIstDateTime, isIndianMarketOpen } from '@/utils/formatters';

interface MarketHeaderProps {
  connectionStatus: ConnectionStatus;
  lastUpdated: string;
  lastSuccessfulSync: number;
  updateNotice: string | null;
}

const statusContent = {
  live: { label: 'LIVE', icon: Radio },
  connecting: { label: 'CONNECTING', icon: Activity },
  reconnecting: { label: 'RECONNECTING', icon: RotateCw },
  offline: { label: 'OFFLINE', icon: WifiOff },
} as const;

export const MarketHeader = ({
  connectionStatus,
  lastUpdated,
  lastSuccessfulSync,
  updateNotice,
}: MarketHeaderProps) => {
  const [marketOpen, setMarketOpen] = useState(isIndianMarketOpen);
  useEffect(() => {
    const timer = window.setInterval(() => setMarketOpen(isIndianMarketOpen()), 30_000);
    return () => window.clearInterval(timer);
  }, []);

  const visibleStatus: ConnectionStatus = marketOpen ? connectionStatus : 'offline';
  const current = statusContent[visibleStatus];
  const Icon = current.icon;
  return (
    <header className="market-header">
      <div className="brand-lockup">
        <div className="brand-mark" aria-hidden="true">
          <span>TX</span>
        </div>
        <div>
          <h1>TradeXRadar</h1>
          <p>Market intelligence · NSE opportunity radar</p>
        </div>
      </div>
      <nav className="primary-nav" aria-label="Dashboard sections">
        <a href="#market-overview">Overview</a>
        <a className="is-active" href="#opportunity-radar">
          Opportunity radar
        </a>
        <a href="#market-news">News</a>
      </nav>
      <div className="live-cluster" aria-live="polite">
        <div
          className={`connection-pill connection-pill--${visibleStatus}`}
          title={marketOpen ? undefined : 'Market closed outside NSE hours'}
        >
          <Icon size={14} aria-hidden="true" />
          {current.label}
        </div>
        <div className="update-stamp">
          <span>{updateNotice ?? 'Last updated'}</span>
          <strong>{lastUpdated ? formatIstDateTime(lastUpdated) : 'Waiting for data'}</strong>
          <small title={lastSuccessfulSync ? formatIstDateTime(lastSuccessfulSync) : undefined}>
            {lastSuccessfulSync ? 'Feed synced successfully' : 'Sync pending'}
          </small>
        </div>
      </div>
    </header>
  );
};
